// ID-free, first-party product events (MVP §4.1). No cookies, no identifiers, no IP or user-agent strings
// leave the device: only coarse classes and buckets. Sent with sendBeacon after the page has painted.
export type EventName =
  | "page_view" | "first_move" | "game_end" | "daily_view" | "daily_complete"
  | "return_bucket" | "storage_state" | "settings_change" | "print" | "error" | "install_tip" | "transfer" | "reminder" | "share"

type Props = Record<string, string | number | boolean>
const ENDPOINT = "/e"
const queue: { e: EventName; p: Props }[] = []
let flushTimer: number | undefined

export function referrerClass(ref: string, here: string): string {
  if (!ref) return "direct-or-bookmark"
  let host = ""
  try { host = new URL(ref).hostname } catch { return "none" }
  if (host === new URL(here).hostname) return "internal"
  if (/(^|\.)google\./.test(host)) return "search-google"
  if (/(^|\.)bing\.com$/.test(host)) return "search-bing"
  if (/duckduckgo|yahoo|ecosia|brave|yandex|baidu|startpage/.test(host)) return "other-search"
  return "referral"
}

export function deviceClass(width: number, coarse: boolean): string {
  if (coarse && width < 600) return "phone"
  if (coarse) return "tablet"
  return width < 600 ? "phone" : "desktop"
}

export function browserFamily(ua: string): string {
  if (/Edg\//.test(ua)) return "edge"
  if (/Firefox\//.test(ua)) return "firefox"
  if (/Chrome\/|CriOS\//.test(ua)) return "chromium"
  if (/Safari\//.test(ua)) return /iPad|iPhone|Macintosh.*Mobile/.test(ua) || (ua.includes("Macintosh") && "ontouchend" in globalThis) ? "safari-ios" : "safari"
  return "other"
}

/** Days since this browser's previous visit, as a bucket (computed on the device; nothing is sent but the bucket). */
export function returnBucket(lastISO: string | null, todayISO: string): string {
  if (!lastISO) return "new"
  const days = Math.round((Date.parse(`${todayISO}T00:00:00Z`) - Date.parse(`${lastISO}T00:00:00Z`)) / 86_400_000)
  if (days <= 0) return "same-day"
  if (days === 1) return "1"
  if (days <= 7) return "2-7"
  if (days <= 30) return "8-30"
  return "over-30"
}

/** Pages opened in this visit (this tab's session), as a band. Partner traffic gates are in pageviews, ours in sessions. */
export function pagesBucket(n: number): string {
  if (n <= 1) return "1"
  if (n === 2) return "2"
  if (n <= 5) return "3-5"
  if (n <= 10) return "6-10"
  return "11+"
}

export function durationBucket(ms: number): string {
  const s = ms / 1000
  if (s < 30) return "<30s"
  if (s < 120) return "30s-2m"
  if (s < 600) return "2-10m"
  if (s < 1800) return "10-30m"
  return "30m+"
}

function context(): Props {
  const nav = navigator as Navigator & { webdriver?: boolean }
  return {
    path: location.pathname,
    device: deviceClass(window.innerWidth, matchMedia("(pointer: coarse)").matches),
    browser: browserFamily(navigator.userAgent),
    display: matchMedia("(display-mode: standalone)").matches ? "standalone" : "browser",
    ivt: nav.webdriver === true,
  }
}

export function track(e: EventName, p: Props = {}): void {
  queue.push({ e, p: { ...context(), ...p } })
  if (flushTimer === undefined) flushTimer = window.setTimeout(flush, 1500)
}

export function flush(): void {
  flushTimer = undefined
  if (!queue.length) return
  const body = JSON.stringify({ events: queue.splice(0, 20) })
  try {
    if (!navigator.sendBeacon?.(ENDPOINT, new Blob([body], { type: "application/json" }))) {
      void fetch(ENDPOINT, { method: "POST", body, keepalive: true, headers: { "Content-Type": "application/json" }, credentials: "omit" })
    }
  } catch { /* measurement must never break play */ }
}
